// @flow

import { useOpenedIncomingPayment } from '@tectonics/prs/incoming-payment/context';
import { type PaymentSpec, getPaymentMethodByCode, usePaymentSpec } from '@tectonics/prs/payment-spec';
import { type UserInputValues, useUserInputValues } from '@tectonics/prs/user-input-values';
import { detectCardBrand } from '@tectonics/prs/card-brand';
import { type OpenedIncomingPayment } from '@tectonics/prs/incoming-payment/types';
import {
  getCheckoutPaymentOptions,
  getCheckoutPaymentOptionsV2,
} from '@tectonics/prs/incoming-payment/get-checkout-payment-options';

export function useCurrentPaymentTypeOptionalFields(fieldName: string): boolean {
  const incomingPayment = useOpenedIncomingPayment();
  const [userInputValues] = useUserInputValues();
  const spec = usePaymentSpec();

  const optionalFields = getCurrentPaymentMethodOptionalFields(incomingPayment, userInputValues, spec);

  return !optionalFields ? false : optionalFields.includes(fieldName);
}

export function getCurrentPaymentMethodOptionalFields(incomingPayment: OpenedIncomingPayment, userInputValues: UserInputValues, spec: PaymentSpec): ?Array<string> {
  if (!incomingPayment) {
    return null;
  }
  let checkoutPaymentOptions = getCheckoutPaymentOptionsV2(incomingPayment);

  if (!checkoutPaymentOptions) {
    checkoutPaymentOptions = getCheckoutPaymentOptions(incomingPayment);
  }

  const selectedPaymentMethod = getPaymentMethodByCode(userInputValues['selectedPaymentMethod'], spec);
  let paymentMethodCode = selectedPaymentMethod ? selectedPaymentMethod.code : null;

  if (!paymentMethodCode) {
    return null;
  }

  let paymentTypeCode = paymentMethodCode;
  if (paymentMethodCode === 'boleto' || paymentMethodCode === 'oxxo') {
    paymentTypeCode = paymentMethodCode;
    paymentMethodCode = 'cash';
  }

  if (incomingPayment.country === 'ec' && (paymentMethodCode === 'safetypay-cash' || paymentMethodCode === 'safetypay-online')) {
    paymentTypeCode = paymentMethodCode;
    paymentMethodCode = 'cash';
  }

  if (!checkoutPaymentOptions) {
    return null;
  }

  const paymentTypeGroupOptionalFields = checkoutPaymentOptions[paymentMethodCode+'_optional_fields'];

  if (!paymentTypeGroupOptionalFields) {
    return [...checkoutPaymentOptions['common_optional_fields'] || []];
  }

  const optionalFieldsKey = getPaymentMethodOptionalFieldsKey(paymentTypeCode, userInputValues);

  return [
    ...checkoutPaymentOptions['common_optional_fields'] || [],
    ...paymentTypeGroupOptionalFields['common_optional_fields'] || '',
    ...paymentTypeGroupOptionalFields[optionalFieldsKey] || '',
  ];
}

export function hasOptionalField(optionalFields: ?Array<string>, desiredField: string): boolean {
  return optionalFields ? optionalFields.includes(desiredField) : false;
}

function getPaymentMethodOptionalFieldsKey(paymentMethodCode: string, userInputValues: UserInputValues): string {
  const cardBrand = detectCardBrand(userInputValues['cardNumber']);
  const selectedBankCode = userInputValues['selectedBankCode'];
  const selectedEWallet = userInputValues['selectedEWallet'];
  const selectedTefBank = userInputValues['selectedTefBank'];
  const selectedEftBank = userInputValues['selectedEftBank'];

  switch (paymentMethodCode) {
    case 'pix':
    case 'banktransfer':
    case 'oxxo':
    case 'oxxopay':
    case 'google':
    case 'spei':
    case 'multicaja':
    case 'servipag':
    case 'sencillito':
    case 'webpay':
    case 'pagoefectivo':
    case 'safetypay':
    case 'safetypay-cash':
    case 'safetypay-online':
    case 'debitcard':
    case 'externalpayprovider':
    case 'qr':
      return paymentMethodCode;

    case 'boleto':
      return paymentMethodCode;

    case 'creditcard':
    case 'vouchercard':
    case 'multibeneficios':
      return String(cardBrand);

    case 'cash':
      return selectedBankCode;

    case 'ewallet':
      return userInputValues['selectedMobileMoneyNetwork'] || selectedEWallet;

    case 'tef':
      return selectedTefBank;

    case 'eft':
      return selectedEftBank;
    default:
      return '';
  }
}
